
// Remote version checking functionality
import type { VersionInfo } from './versionManager';

interface RemoteVersionInfo {
  version: string;
  buildId: string;
  forceRefresh: boolean;
  cacheControl?: {
    enabled: boolean;
    maxAge: number;
    staleWhileRevalidate: number;
  };
  lastUpdateCheck?: string;
}

// Keep track of the last check time to prevent excessive checks
let lastCheckTime = 0;
// Reduced interval to 10 seconds to make sure we're checking more frequently
const MIN_CHECK_INTERVAL = 10000; // Check every 10 seconds

export const checkRemoteVersion = async (): Promise<RemoteVersionInfo | null> => {
  try {
    // Prevent excessive checks
    const now = Date.now();
    if (now - lastCheckTime < MIN_CHECK_INTERVAL) {
      console.log('Skipping remote version check - checked recently');
      return null;
    }
    
    lastCheckTime = now;
    
    // Always use a unique timestamp to bypass any caching
    const response = await fetch(`/.well-known/version.json?_t=${Date.now()}`, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      cache: 'no-store'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (e) {
    console.warn('Could not fetch remote version:', e);
    return null;
  }
};

export const shouldForceUpdate = (
  currentInfo: VersionInfo,
  storedInfo: string | null,
  remoteInfo: RemoteVersionInfo | null
): boolean => {
  // If remote explicitly requests a refresh, honor it
  if (remoteInfo?.forceRefresh === true) {
    console.log('Remote version is requesting force refresh');
    return true;
  }
  
  // If remote version is different from what we have, update
  if (remoteInfo && remoteInfo.buildId !== currentInfo.buildId) {
    console.log('Remote version differs from local version');
    return true;
  }

  // Check local version differences (only if we have stored info)
  if (!storedInfo) return false;

  try {
    const parsedInfo = JSON.parse(storedInfo) as VersionInfo;
    return parsedInfo.version !== currentInfo.version || 
           parsedInfo.buildId !== currentInfo.buildId;
  } catch (e) {
    console.warn('Error parsing stored version info:', e);
    return false;
  }
};
